import { useEffect } from 'react';

import {
  faCheck,
  faSpinner,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useNavigate } from 'react-router-dom';

import { useLogout } from '../hooks/useLogout';
import { useCountdown } from '../utils/useCountdown';
import Button from '../components/Button';

export enum VerificationStatus {
  VERIFYING = 'VERIFYING',
  VERIFIED = 'VERIFIED',
  NO_TOKEN = 'NO_TOKEN',
  INVALID_TOKEN = 'INVALID_TOKEN',
}

interface ValidateEmailStatusProps {
  status: VerificationStatus;
  isAgentContext?: boolean;
}

const ValidateEmailStatus: React.FC<ValidateEmailStatusProps> = ({
  status,
  isAgentContext,
}) => {
  const navigate = useNavigate();
  const logout = useLogout();

  const handleRedirect = () => {
    logout();
    // Use isAgentContext if provided, otherwise default to regular login
    navigate(isAgentContext ? '/agent-login' : '/login');
  };

  const { seconds, start } = useCountdown({
    initialSeconds: 10,
    onComplete: () => {
      handleRedirect();
    },
  });

  useEffect(() => {
    if (status === VerificationStatus.VERIFIED) {
      start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (status === VerificationStatus.VERIFYING) {
    return (
      <div className='flex items-center justify-center space-x-3 flex-col md:flex-row'>
        <FontAwesomeIcon
          icon={faSpinner}
          className='text-primary-600 animate-spin text-base md:text-lg'
        />
        <p className='text-lg md:text-xl font-bold text-gray-800'>
          Please wait while we verify your email address...
        </p>
      </div>
    );
  }

  if (status === VerificationStatus.VERIFIED) {
    return (
      <div className='flex flex-col items-center space-y-4 p-5'>
        <div className='flex items-center justify-center space-x-3'>
          <FontAwesomeIcon
            icon={faCheck}
            className='text-green-600'
            size='lg'
          />
          <p className='text-xl md:text-2xl font-bold text-green-600'>
            Email Verified Successfully!
          </p>
        </div>
        <div className='space-y-4 flex flex-col items-center'>
          <p className='text-sm md:text-base text-gray-600'>
            You will be redirected in {seconds} seconds
          </p>
          <Button
            onPress={handleRedirect}
            label='Continue to Login'
            variant='primary'
          />
        </div>
      </div>
    );
  }

  if (status === VerificationStatus.NO_TOKEN) {
    return (
      <div className='flex flex-col items-center space-y-4 p-5'>
        <div className='flex items-center justify-center space-x-3'>
          <FontAwesomeIcon icon={faXmark} className='text-red-600' size='lg' />
          <p className='text-xl md:text-2xl font-bold text-red-600'>
            Invalid Email Verification Link
          </p>
        </div>
        <div className='space-y-4 flex flex-col items-center'>
          <p className='text-sm md:text-base text-gray-600'>
            This link is invalid. Please log in again to resend your
            verification email.
          </p>
          <Button
            onPress={handleRedirect}
            label='Return to Login'
            variant='primary'
          />
        </div>
      </div>
    );
  }

  if (status === VerificationStatus.INVALID_TOKEN) {
    return (
      <div className='flex flex-col items-center space-y-4 p-5'>
        <div className='flex items-center justify-center space-x-3'>
          <FontAwesomeIcon icon={faXmark} className='text-red-600' size='lg' />
          <p className='text-xl md:text-2xl font-bold text-red-600'>
            Expired Verification Link
          </p>
        </div>
        <div className='space-y-4 flex flex-col items-center'>
          <p className='text-sm md:text-base text-gray-600'>
            The verification link you have provided is invalid or has expired.
            Please log back in to resend your verification email.
          </p>
          <Button
            onPress={handleRedirect}
            label='Return to Login'
            variant='primary'
          />
        </div>
      </div>
    );
  }

  return null;
};

export default ValidateEmailStatus;
